import * as Collapsible from '@radix-ui/react-collapsible';
import { useMediaQuery, useResizablePanel } from '@vizit/hooks';
import { XCloseIcon } from '@vizit/icons';
import { cva, cx } from '@vizit/theme';
import * as React from 'react';
import { Button, ButtonLevel, ButtonSize } from '../button';
import { Text, TextSize, TextWeight } from '../text';
import { CollapsibleDrawerProvider, useCollapsibleDrawer } from './collapsible-drawer-provider';

// Below 821px the drawer stacks under the page content instead of sitting
// beside it (see the `max-width:820px` rules in the variants below). Resizing a
// full-width stacked panel is meaningless, so every rule here is scoped to
// `min-[821px]`. These must stay literal strings — Tailwind scans source text
// and cannot see class names built from template literals.

/**
 * Mirrors the `min-[821px]` breakpoint used throughout this file's Tailwind
 * classes, for what a class can't express: gating the root's inline
 * `gridTemplateColumns` style and the resize behavior (below) so neither
 * clobbers the stacked (< 821px) tier's own full-width grid. `useMediaQuery` —
 * not `window.innerWidth` — so this only re-renders when the
 * side-by-side/stacked boundary is actually crossed.
 *
 * Consumers that stack at a wider breakpoint (via their own `max-width` class
 * overrides on the root) pass a matching query through `sideBySideMediaQuery`
 * so the inline grid pin and the resize handle disengage at the same width as
 * their classes.
 */
const SIDE_BY_SIDE_MEDIA_QUERY = '(min-width: 821px)';

/**
 * Root layout while a resizable drawer is open. `relative` gives the panel,
 * once it floats, an anchor to position `absolute` against. The grid itself
 * is intentionally untouched here — see `gridTemplateColumns` in
 * `CollapsibleDrawer`, which pins the column at `minWidth` for the drawer's
 * entire open lifetime so the page content lays out once and never reflows
 * as the panel is dragged wider.
 */
const RESIZABLE_ROOT_CLASSES = 'relative';

/**
 * Panel classes shared by the docked (`width === minWidth`) and floating
 * (`width > minWidth`) states alike, at the side-by-side tier.
 *
 * Only `box-shadow` transitions, and only while the drawer is actually being
 * resized-through, not dragged: width/position must track the pointer 1:1,
 * so animating anything else here would make the panel visibly lag behind
 * the cursor.
 */
const RESIZABLE_CONTENT_CLASSES = [
  'transition-shadow',
  'duration-150',
  'ease-out',
  'motion-reduce:transition-none',
].join(' ');

/**
 * Docked, the panel stays in flow and simply fills its `minWidth` grid column.
 * `min-[821px]:relative` gives the resize handle — an absolutely-positioned
 * child — an anchor to sit on the panel's edge rather than the page's.
 *
 * Kept in its own docked-only constant so it is never emitted alongside
 * `FLOATING_CONTENT_CLASSES`' `min-[821px]:absolute`: the two are the same
 * Tailwind position utility at the same breakpoint, and which one wins would
 * otherwise rest on `cx`'s merge order rather than on the state itself.
 */
const DOCKED_CONTENT_CLASSES = 'min-[821px]:relative';

/**
 * Promotes the panel out of flow once it's wider than the docked baseline —
 * it now overhangs its (unchanged) grid column and must cover the page
 * content, which hasn't moved, instead of squeezing it. Width comes from the
 * `--drawer-width` custom property.
 */
const FLOATING_CONTENT_CLASSES = [
  'min-[821px]:absolute',
  'min-[821px]:top-0',
  'min-[821px]:bottom-0',
  'min-[821px]:w-[var(--drawer-width)]',
  'min-[821px]:max-w-full',
  // Above the page content, but below dnd-kit's drag overlay so an asset being
  // dragged out of the drawer still renders on top of it.
  'min-[821px]:z-30',
  // Small and faint on purpose — just enough depth to read as overlaying the
  // page content rather than sitting beside it. No token fits: the elevation
  // scale's offsets are all vertical, while a full-height panel casts sideways,
  // so this is offsetless blur only. Docked (width === minWidth) carries no
  // shadow at all; see DOCKED_CONTENT_CLASSES.
  'min-[821px]:shadow-[0_0_8px_rgba(0,0,0,0.16)]',
].join(' ');

/** Side-specific anchoring for the floating (out-of-flow) panel. */
const FLOATING_CONTENT_SIDE_CLASSES = {
  left: 'min-[821px]:left-0',
  right: 'min-[821px]:right-0',
} as const;

/**
 * Positions the handle on the panel's inner edge, opposite its anchor, and
 * centres it on the border via the half-width translate — so its `w-12` (24px)
 * hit area straddles the edge, ~12px into the panel and ~12px into the page.
 *
 * The width itself lives on the handle element below rather than here: 24px is
 * the minimum pointer target regardless of which side the panel is anchored to,
 * so duplicating it across both variants would only invite them to drift.
 */
const RESIZE_HANDLE_SIDE_CLASSES = {
  left: 'right-0 translate-x-1/2',
  right: 'left-0 -translate-x-1/2',
} as const;

// - Default mode: Uses fractional columns 1fr_1fr_1fr_1fr
// - Custom width mode: Uses fixed pixel columns 1fr_524px (xl > 1280px) and 1fr_366px (<1280px)
const rootVariants = cva({
  base: 'grid w-full h-full grid-rows-[minmax(0,1fr)] overflow-hidden transition-all ease-in-out duration-300 [@media(max-width:820px)]:max-h-screen',
  variants: {
    side: {
      left: '',
      right: '',
    },
    isOpen: {
      true: '',
      false: '',
    },
    hasCustomWidth: {
      true: '',
      false: '',
    },
  },
  compoundVariants: [
    // Default grid for standard drawer
    {
      isOpen: true,
      hasCustomWidth: false,
      className:
        'grid-cols-[1fr_1fr_1fr_1fr] max-lg:grid-cols-[1fr_1fr_1fr] [@media(max-width:820px)]:grid-rows-2 [@media(max-width:820px)]:grid-cols-1',
    },
    {
      isOpen: false,
      side: 'right',
      hasCustomWidth: false,
      className:
        'grid-cols-[1fr_1fr_1fr_0fr] max-lg:grid-cols-[1fr_1fr_0fr] [@media(max-width:820px)]:grid-rows-1 [@media(max-width:820px)]:grid-cols-1',
    },
    {
      isOpen: false,
      side: 'left',
      hasCustomWidth: false,
      className:
        'grid-cols-[0fr_1fr_1fr_1fr] max-lg:grid-cols-[0fr_1fr_1fr] [@media(max-width:820px)]:grid-rows-1 [@media(max-width:820px)]:grid-cols-1',
    },
    // Custom width grid for fixed-width drawer
    {
      isOpen: true,
      side: 'right',
      hasCustomWidth: true,
      className:
        'grid-cols-[1fr_524px] max-xl:grid-cols-[1fr_366px] [@media(max-width:820px)]:grid-rows-2 [@media(max-width:820px)]:grid-cols-1',
    },
    {
      isOpen: false,
      side: 'right',
      hasCustomWidth: true,
      className: 'grid-cols-[1fr_0px] [@media(max-width:820px)]:grid-rows-1 [@media(max-width:820px)]:grid-cols-1',
    },
    {
      isOpen: true,
      side: 'left',
      hasCustomWidth: true,
      className:
        'grid-cols-[524px_1fr] max-xl:grid-cols-[366px_1fr] [@media(max-width:820px)]:grid-rows-2 [@media(max-width:820px)]:grid-cols-1',
    },
    {
      isOpen: false,
      side: 'left',
      hasCustomWidth: true,
      className: 'grid-cols-[0px_1fr] [@media(max-width:820px)]:grid-rows-1 [@media(max-width:820px)]:grid-cols-1',
    },
  ],
});

/**
 * The panel itself: grid placement, chrome, and (when resizable) positioning.
 * Deliberately NOT a scroll container — scrolling lives on the inner
 * `SCROLL_CONTAINER_CLASSES` element — so the absolutely-positioned resize
 * handle can anchor to the panel's edge without being clipped by `overflow`
 * (which would halve its 24px hit area) or carried off-screen as the content
 * scrolls.
 */
const panelVariants = cva({
  base: 'bg-primary h-full max-h-screen data-[state=open]:animate-fade-in col-span-1 [@media(max-width:820px)]:row-span-1 [@media(max-width:820px)]:border-t-[1px] [@media(max-width:820px)]:border-t-gray-utility-200',
  variants: {
    side: {
      left: 'col-start-1 row-end-[none] border-r-[1px] border-r-gray-utility-200',
      right: 'border-l-[1px] border-l-gray-utility-200',
    },
  },
});

/**
 * Scroll container between the panel and its content. Carries the vertical
 * padding, and receives the caller's `className`, so existing consumer classes
 * keep their meaning: `py-0` still merges against the padding, `scrollbar-*`
 * still styles the element that actually scrolls, and `<section>` stays its
 * direct child for `[&>section]` selectors.
 */
const SCROLL_CONTAINER_CLASSES = 'h-full w-full overflow-y-auto py-12';

const triggerContainerVariants = cva({
  base: 'flex flex-col gap-4 w-full h-full min-h-0 min-w-0 flex-wrap [@media(max-width:820px)]:col-span-1 [@media(max-width:820px)]:row-span-1',
  variants: {
    side: {
      left: '',
      right: '',
    },
    isOpen: {
      true: '',
      false: '',
    },
    hasCustomWidth: {
      true: '',
      false: '',
    },
  },
  compoundVariants: [
    {
      hasCustomWidth: false,
      className: 'col-span-3 max-lg:col-span-2',
    },
    {
      isOpen: true,
      side: 'left',
      hasCustomWidth: false,
      className:
        'col-start-2 col-end-5 max-lg:col-start-2 max-lg:col-end-4 [@media(max-width:820px)]:col-start-1 [@media(max-width:820px)]:col-end-2',
    },
    {
      isOpen: false,
      side: 'left',
      hasCustomWidth: false,
      className:
        'col-start-1 col-end-5 max-lg:col-start-1 max-lg:col-end-4 [@media(max-width:820px)]:col-start-1 [@media(max-width:820px)]:col-end-2',
    },
    // Custom width trigger container (2-column grid)
    {
      hasCustomWidth: true,
      className: 'col-span-1',
    },
    {
      isOpen: true,
      side: 'left',
      hasCustomWidth: true,
      className: 'col-start-2 [@media(max-width:820px)]:col-start-1 [@media(max-width:820px)]:col-end-2',
    },
    {
      isOpen: false,
      side: 'left',
      hasCustomWidth: true,
      className: 'col-start-1 col-end-3 [@media(max-width:820px)]:col-start-1 [@media(max-width:820px)]:col-end-2',
    },
  ],
});

interface CollapsibleDrawerProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * The title of the drawer.
   */
  title: string;
  /**
   * Whether the drawer is open by default.
   */
  defaultOpen?: boolean;
  /**
   * Whether the drawer is open.
   */
  isOpen?: boolean;
  /**
   * Whether the drawer is disabled.
   */
  isDisabled?: boolean;
  /**
   * The side of the drawer.
   */
  side?: 'left' | 'right';
  /**
   * Custom width classes for the drawer content.
   */
  contentWidth?: string;
  /**
   * Whether the drawer can be resized by dragging a handle on its inner edge.
   * When enabled, the open panel overlays the page content instead of taking a
   * grid column from it.
   */
  isResizable?: boolean;
  /**
   * Width in px the resizable drawer opens at before the user has resized it.
   * Ignored unless `isResizable` is set.
   * @default 366
   */
  defaultWidth?: number;
  /**
   * Smallest width in px the drawer can be dragged to.
   * @default 280
   */
  minWidth?: number;
  /**
   * Largest width in px. Defaults to a fraction of the viewport so the content
   * behind the drawer — and any drop targets in it — stays reachable.
   */
  maxWidth?: number;
  /**
   * `localStorage` key the chosen width is persisted under. Omit to reset the
   * width on every mount.
   */
  resizeStorageKey?: string;
  /**
   * Accessible label for the resize handle.
   * @default `Resize ${title} panel`
   */
  resizeHandleLabel?: string;
  /**
   * Media query for the tier where the drawer sits beside the page content.
   * Below it the drawer stacks under the content: the resize handle unmounts
   * and the root's inline grid-column pin is released. Consumers that stack at
   * a wider breakpoint than the shared 821px default (via their own
   * `max-width` class overrides) pass a matching query so both move together.
   * @default '(min-width: 821px)'
   */
  sideBySideMediaQuery?: string;
  /**
   * The function to call when the drawer is opened or closed.
   */
  onOpenChange?(open: boolean): void;
}

type CollapsibleDrawerTriggerProps = Omit<Collapsible.CollapsibleTriggerProps, 'asChild'>;
interface CollapsibleDrawerContentProps extends Collapsible.CollapsibleContentProps {
  /**
   * Custom header content to replace the default header.
   * If provided, the default title and close button will not be rendered.
   */
  headerContent?: React.ReactNode;
  /**
   * Classes for the panel element itself (grid placement, borders, chrome).
   * `className` lands on the inner scroll container instead — see
   * `SCROLL_CONTAINER_CLASSES` — so overrides of the panel's own classes (e.g.
   * a stacked-tier border) must come through here to merge against them.
   */
  panelClassName?: string;
}

function CollapsibleDrawerTrigger(props: CollapsibleDrawerTriggerProps) {
  const { children, className, ...other } = props;

  const { disabled, open, onOpenChange } = useCollapsibleDrawer();

  const handleOnClick = React.useCallback(() => {
    onOpenChange?.(!open);
  }, [open, onOpenChange]);

  return (
    <Collapsible.Trigger disabled={disabled} asChild onClick={handleOnClick} {...other}>
      {children}
    </Collapsible.Trigger>
  );
}

/**
 * Drag handle for a resizable drawer. Rendered on the panel's inner edge — the
 * left edge of a right-anchored drawer — and centred on it so the grab area
 * straddles the border instead of eating into the panel's content.
 */
function CollapsibleDrawerResizeHandle(props: { side: 'left' | 'right' }) {
  const { side } = props;
  const { resize } = useCollapsibleDrawer();

  if (!resize) return null;

  const { isResizing, separatorProps } = resize;

  return (
    <div
      {...separatorProps}
      // Hidden on the stacked tier, where the panel is full-width and there is
      // nothing to resize against.
      className={cx(
        'group absolute top-0 bottom-0 z-10 hidden min-[821px]:flex w-12 cursor-col-resize touch-none items-center justify-center',
        'outline-none focus-visible:ring-2 focus-visible:ring-brand-600',
        RESIZE_HANDLE_SIDE_CLASSES[side],
      )}
    >
      {/* The visible rule is a thin child so the hit area stays a full 24px
          while the affordance itself reads as a 3px border on the panel edge. */}
      <div
        aria-hidden="true"
        className={cx(
          'h-full w-[5px] rounded-full transition-colors duration-150',
          'group-hover:bg-indigo-300 group-focus-visible:bg-indigo-300',
          isResizing ? 'bg-indigo-300' : 'bg-transparent',
        )}
      />
    </div>
  );
}

function CollapsibleDrawerContent(props: CollapsibleDrawerContentProps) {
  const { children, className, panelClassName, headerContent, style, ...other } = props;

  const { disabled, side, title, onOpenChange, resize } = useCollapsibleDrawer();

  const handleOnClose = () => onOpenChange?.(false);

  const defaultHeader = (
    <div className="flex items-center justify-between gap-2 px-12 pb-8">
      <Text weight={TextWeight.Semibold} size={TextSize.Lg}>
        {title}
      </Text>
      {!disabled && (
        <Button
          onPress={handleOnClose}
          aria-label="Close"
          size={ButtonSize.Sm}
          level={ButtonLevel.Ghost}
          iconStart={<XCloseIcon size={20} />}
        />
      )}
    </div>
  );

  return (
    <Collapsible.Content
      className={cx(
        panelVariants({ side }),
        'w-full',
        resize && RESIZABLE_CONTENT_CLASSES,
        resize && !resize.isFloating && DOCKED_CONTENT_CLASSES,
        resize?.isFloating && FLOATING_CONTENT_CLASSES,
        resize?.isFloating && FLOATING_CONTENT_SIDE_CLASSES[side],
        panelClassName,
      )}
      // Caller styles first, so `--drawer-width` survives them: it is what the
      // floating panel's `w-[var(--drawer-width)]` reads, and `other` spreading
      // last would otherwise let a caller's `style` replace this wholesale and
      // silently collapse the panel's width.
      style={resize ? ({ ...style, '--drawer-width': `${resize.width}px` } as React.CSSProperties) : style}
      {...other}
    >
      {/* The handle is a sibling of the scroll container, not a child: inside
          it, the scroll box's overflow clips the half of the hit area that
          overhangs the panel edge, and `top-0 bottom-0` would track the
          scrolled content instead of the panel. */}
      {resize && <CollapsibleDrawerResizeHandle side={side} />}
      <div className={cx(SCROLL_CONTAINER_CLASSES, className)}>
        <section aria-label={title}>
          {headerContent ?? defaultHeader}
          {children}
        </section>
      </div>
    </Collapsible.Content>
  );
}

function CollapsibleDrawer(props: CollapsibleDrawerProps) {
  const {
    title,
    children: childrenProp,
    className,
    defaultOpen = false,
    isDisabled = false,
    isOpen: isOpenProp,
    side = 'right',
    contentWidth,
    isResizable = false,
    defaultWidth = 366,
    minWidth = 280,
    maxWidth,
    resizeStorageKey,
    resizeHandleLabel,
    sideBySideMediaQuery = SIDE_BY_SIDE_MEDIA_QUERY,
    onOpenChange,
    style,
    ...other
  } = props;

  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(defaultOpen);

  const isControlled = React.useMemo(() => isOpenProp !== undefined, [isOpenProp]);
  const open = React.useMemo(
    () => (isControlled ? isOpenProp : uncontrolledOpen),
    [isControlled, isOpenProp, uncontrolledOpen],
  );

  const handleOpenChange = (open: boolean) => {
    if (isDisabled) return;

    if (!isControlled) {
      setUncontrolledOpen(open);
    }

    onOpenChange?.(open);
  };

  const children = React.Children.toArray(childrenProp).filter((child) => {
    return React.isValidElement(child) && child.type !== CollapsibleDrawerContent;
  });

  const sidebar = React.Children.toArray(childrenProp).find((child) => {
    return React.isValidElement(child) && child.type === CollapsibleDrawerContent;
  });

  const hasCustomWidth = !!contentWidth;

  // Called unconditionally (Rules of Hooks), but inert unless this drawer is
  // actually resizable — otherwise every non-resizable drawer on the page would
  // hold a `resize` listener and a persistence effect it can never use.
  const { width, isResizing, separatorProps } = useResizablePanel({
    defaultWidth,
    minWidth,
    maxWidth,
    storageKey: resizeStorageKey,
    side,
    isEnabled: isResizable,
  });

  // The docked <-> floating threshold: the panel is "docked" (in-flow, flush,
  // no shadow) at exactly its baseline and "floating" (out-of-flow, overlaying
  // the page content, shadowed) the moment it's dragged past it. Derived
  // rather than tracked as its own piece of hook state, since it's a pure
  // function of width and minWidth.
  const isFloating = width > minWidth;

  // Gates the resize state and the inline grid style below so neither fights
  // the stacked tier's own full-width grid, which is expressed purely in
  // Tailwind classes and has no equivalent JS-side signal otherwise. Like
  // `useResizablePanel` above, inert unless this drawer is resizable — only
  // resize behavior reads the tier, so a non-resizable drawer shouldn't hold
  // a `matchMedia` listener for it.
  const isSideBySideTier = useMediaQuery(sideBySideMediaQuery, { isEnabled: isResizable });

  // Only surface resize state while the drawer is actually open — a closed
  // drawer must keep collapsing to a zero-width grid column rather than being
  // pinned open at the persisted width — and only at the side-by-side tier: a
  // stacked panel is full-width, so there is nothing to resize against and the
  // handle must unmount rather than sit on the panel's edge.
  const resize =
    isResizable && open && isSideBySideTier ? { width, isResizing, separatorProps, isFloating } : undefined;

  // Pinned at `minWidth` for the drawer's entire open lifetime — docked or
  // floating alike — so the page content's column lays out once and never
  // reflows as the panel is dragged wider. Tailwind can't express this: the
  // column width is a runtime value that differs per consumer (524/366/278),
  // and arbitrary values can't be built from a template literal.
  const gridTemplateColumns = resize ? (side === 'right' ? `1fr ${minWidth}px` : `${minWidth}px 1fr`) : undefined;

  return (
    <CollapsibleDrawerProvider
      value={{
        title,
        open,
        onOpenChange: handleOpenChange,
        disabled: isDisabled,
        side,
        contentWidth,
        resize: resize && {
          ...resize,
          separatorProps: { ...resize.separatorProps, 'aria-label': resizeHandleLabel ?? `Resize ${title} panel` },
        },
        ...other,
      }}
    >
      <Collapsible.Root
        open={open}
        onOpenChange={handleOpenChange}
        className={cx(
          rootVariants({ side, isOpen: open, hasCustomWidth }),
          resize && RESIZABLE_ROOT_CLASSES,
          // Suppress text selection and keep the resize cursor while dragging,
          // even as the pointer leaves the handle. Read through `resize` rather
          // than the raw flag: `isResizing` is only cleared by the handle's own
          // pointer-end handler, so a drawer closed mid-drag unmounts the handle
          // with the flag still set, and an ungated class would strand the page
          // content under a resize cursor it can't select text through.
          resize?.isResizing && 'cursor-col-resize select-none',
          className,
        )}
        style={gridTemplateColumns ? { ...style, gridTemplateColumns } : style}
        {...other}
      >
        <div className={triggerContainerVariants({ side, isOpen: open, hasCustomWidth })}>{children}</div>
        {sidebar}
      </Collapsible.Root>
    </CollapsibleDrawerProvider>
  );
}

export { CollapsibleDrawer, CollapsibleDrawerTrigger, CollapsibleDrawerContent };
export type { CollapsibleDrawerProps, CollapsibleDrawerTriggerProps, CollapsibleDrawerContentProps };
